import { useEffect, useRef, useState } from "react";
import { useLanguage } from "../contexts/LanguageContext";

const CASSETTE_A =
  "https://customer-assets.emergentagent.com/job_clickable-image-nav/artifacts/mchwpng1_IMG_7751.png";
const CASSETTE_B =
  "https://customer-assets.emergentagent.com/job_clickable-image-nav/artifacts/bcqh0pvd_IMG_7752.png";

export default function Cassette({
  side,
  audioSrc,
  testIdPrefix,
  autoPlay = false,
  downloadName,
  externalAudio, // optional: { isPlaying, play, pause, stop, seek, download, error }
}) {
  const audioRef = useRef(null);
  const recordClickTimerRef = useRef(null);
  const [internalPlaying, setInternalPlaying] = useState(false);
  const [internalError, setInternalError] = useState(!audioSrc);
  const { t } = useLanguage();

  const image = side === "A" ? CASSETTE_A : CASSETTE_B;
  // Side C uses cassette B visual (alternative tape look)
  const ctrl = externalAudio;
  const isPlaying = ctrl ? ctrl.isPlaying : internalPlaying;
  const audioError = ctrl ? ctrl.error : internalError;

  useEffect(() => {
    if (ctrl) return; // external handles audio
    const audio = audioRef.current;
    if (!audio) return;
    const onEnd = () => setInternalPlaying(false);
    const onErr = () => {
      setInternalError(true);
      setInternalPlaying(false);
    };
    audio.addEventListener("ended", onEnd);
    audio.addEventListener("error", onErr);
    return () => {
      audio.removeEventListener("ended", onEnd);
      audio.removeEventListener("error", onErr);
    };
  }, [ctrl]);

  useEffect(() => {
    if (ctrl) return;
    if (!autoPlay || !audioSrc || internalError) return;
    audioRef.current?.play().then(() => setInternalPlaying(true)).catch(() => {});
  }, [autoPlay, audioSrc, internalError, ctrl]);

  const doPlay = () => {
    if (ctrl) return ctrl.play();
    const audio = audioRef.current;
    if (!audio || internalError) return;
    audio.play().then(() => setInternalPlaying(true)).catch(() => {
      setInternalError(true);
      setInternalPlaying(false);
    });
  };
  const doPause = () => {
    if (ctrl) return ctrl.pause();
    const audio = audioRef.current;
    if (!audio) return;
    audio.pause();
    setInternalPlaying(false);
  };
  const doStop = () => {
    if (ctrl) return ctrl.stop();
    const audio = audioRef.current;
    if (!audio) return;
    audio.pause();
    audio.currentTime = 0;
    setInternalPlaying(false);
  };
  const doSeek = (delta) => {
    if (ctrl) return ctrl.seek(delta);
    const audio = audioRef.current;
    if (!audio) return;
    audio.currentTime = Math.max(0, audio.currentTime + delta);
  };
  const doDownload = () => {
    if (ctrl) return ctrl.download(downloadName);
    if (!audioSrc) return;
    const a = document.createElement("a");
    a.href = audioSrc;
    a.download = downloadName || `${testIdPrefix}.mp3`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  // Record button: SINGLE click does nothing (it's "armed"), DOUBLE click downloads
  const handleRecordClick = () => {
    if (audioError) return;
    if (recordClickTimerRef.current) {
      clearTimeout(recordClickTimerRef.current);
      recordClickTimerRef.current = null;
      doDownload();
      return;
    }
    recordClickTimerRef.current = setTimeout(() => {
      recordClickTimerRef.current = null;
      // single click: do nothing (visual feedback only)
    }, 280);
  };

  return (
    <div
      data-testid={`${testIdPrefix}-wrapper`}
      className="relative flex w-full flex-col items-center"
      style={{ maxWidth: "380px" }}
    >
      <div
        data-testid={`${testIdPrefix}-recorder`}
        className="relative w-full select-none"
        style={{
          background:
            "linear-gradient(180deg, #1F1B18 0%, #15110F 60%, #0E0B09 100%)",
          borderRadius: "12px",
          padding: "18px 18px 16px",
          border: "1px solid #3A322C",
          boxShadow:
            "0 30px 60px -25px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.08), inset 0 -2px 0 rgba(0,0,0,0.5)",
        }}
      >
        <div className="mb-4 flex items-center justify-between">
          <span className="text-[10px] uppercase tracking-[0.3em]" style={{ color: "#C8B690", fontWeight: 700 }}>wer · cassette</span>
          <span className="text-[9px] uppercase tracking-[0.25em]" style={{ color: "#9B8A75", fontWeight: 600 }}>Stereo · Auto Stop</span>
        </div>

        <div
          className="mb-4 flex items-center justify-between rounded px-3 py-2 font-mono text-[11px] sm:text-[13px]"
          style={{
            background: "linear-gradient(180deg, #1A1612 0%, #0A0807 100%)",
            border: "1px solid #2A2521",
            color: "#FF8C42",
            boxShadow: "inset 0 2px 4px rgba(0,0,0,0.7), 0 0 6px rgba(255,140,66,0.15)",
            letterSpacing: "2px",
          }}
        >
          <span>SIDE {side}</span>
          <span>{isPlaying ? "▶ PLAY" : "■ STOP"}</span>
          <span>000</span>
        </div>

        <div
          className="relative overflow-hidden"
          style={{
            background: "linear-gradient(180deg, #0F0C0A 0%, #1A1612 100%)",
            borderRadius: "8px",
            padding: "14px",
            border: "2px solid #2A2521",
            boxShadow: "inset 0 2px 8px rgba(0,0,0,0.7)",
          }}
        >
          <div className="mb-2 flex items-center justify-between text-[8px] uppercase">
            <span style={{ color: "#9B8A75", letterSpacing: "0.2em", fontWeight: 600 }}>Tape Door</span>
            <span style={{ color: "#9B8A75", letterSpacing: "0.2em", fontWeight: 600 }}>wer · 1985</span>
          </div>
          <div className="relative overflow-hidden rounded" style={{ background: "linear-gradient(180deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.25) 100%)", border: "1px solid rgba(255,255,255,0.06)" }}>
            <img src={image} alt={`Cassette side ${side}`} className="block h-auto w-full select-none" draggable={false} />
            <div aria-hidden className="pointer-events-none absolute inset-0" style={{ background: "linear-gradient(135deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0) 35%)" }} />
            <span aria-hidden className={`pointer-events-none absolute ${isPlaying ? "cassette-reel-spin" : ""}`} style={{ left: "30%", top: "48%", width: "15%", aspectRatio: "1", transform: "translate(-50%, -50%)", zIndex: 5 }}>
              <ReelHub />
            </span>
            <span aria-hidden className={`pointer-events-none absolute ${isPlaying ? "cassette-reel-spin" : ""}`} style={{ left: "70%", top: "48%", width: "15%", aspectRatio: "1", transform: "translate(-50%, -50%)", zIndex: 5 }}>
              <ReelHub />
            </span>
            {/* Moving tape band — sits across the bottom tape path of the cassette */}
            <span
              aria-hidden
              className="pointer-events-none absolute overflow-hidden"
              style={{
                left: "38%",
                right: "38%",
                top: "62%",
                height: "5%",
                transform: "translateY(-50%)",
                zIndex: 4,
                background:
                  "linear-gradient(180deg, rgba(110,75,55,0.95) 0%, rgba(70,40,25,1) 50%, rgba(35,18,10,1) 100%)",
                borderTop: "1px solid rgba(255,255,255,0.08)",
                borderBottom: "1px solid rgba(0,0,0,0.7)",
                boxShadow: "inset 0 1px 3px rgba(0,0,0,0.6)",
              }}
            >
              {isPlaying ? (
                <span
                  aria-hidden
                  className="cassette-tape-scroll absolute inset-0 block"
                  style={{
                    backgroundImage:
                      "repeating-linear-gradient(90deg, transparent 0 4px, rgba(255,255,255,0.09) 4px 5px, transparent 5px 10px)",
                  }}
                />
              ) : null}
            </span>
          </div>
        </div>

        <div className="mt-4 flex items-center justify-center gap-5">
          <StatusLed label="Play" on={isPlaying} color="#39E27A" />
          <StatusLed label="Rec" on={false} color="#FF4530" />
        </div>

        <div className="mt-4 grid grid-cols-6 gap-[5px]">
          <MechBtn label="◀◀" ariaLabel="Rewind" testId={`${testIdPrefix}-btn-rewind`} onClick={() => doSeek(-10)} disabled={audioError} />
          <MechBtn label="▶" ariaLabel={t("play")} testId={`${testIdPrefix}-play-pause-button`} onClick={() => (isPlaying ? doPause() : doPlay())} disabled={audioError} active={isPlaying} big />
          <MechBtn label="❚❚" ariaLabel={t("pause")} testId={`${testIdPrefix}-btn-pause`} onClick={doPause} disabled={audioError} />
          <MechBtn label="■" ariaLabel="Stop" testId={`${testIdPrefix}-btn-stop`} onClick={doStop} disabled={audioError} />
          <MechBtn label="●" ariaLabel="Download (doubleclick)" testId={`${testIdPrefix}-btn-rec`} onClick={handleRecordClick} disabled={audioError} recColor title="Doppelklick zum Download" />
          <MechBtn label="▶▶" ariaLabel="Forward" testId={`${testIdPrefix}-btn-forward`} onClick={() => doSeek(10)} disabled={audioError} />
        </div>

        <div className="mt-4 flex items-center justify-between">
          <span className="text-[9px] uppercase tracking-[0.3em]" style={{ color: "#C8B690", fontWeight: 700 }}>Kassettenrekorder</span>
          <span className="text-[8px] uppercase tracking-[0.2em]" style={{ color: "#9B8A75", fontWeight: 600 }}>Model 1985</span>
        </div>
      </div>

      <p className="mt-4 text-xs sm:text-sm" style={{ color: "rgba(26,26,26,0.6)", fontStyle: "italic" }} data-testid={`${testIdPrefix}-download-hint`}>
        ({t("downloadHint")} <span style={{ color: "#B33020", fontWeight: 700 }}>●</span> {t("downloadHintSuffix")})
      </p>

      {audioError ? (
        <p data-testid={`${testIdPrefix}-audio-missing`} className="mt-1 text-sm italic" style={{ color: "rgba(26,26,26,0.5)" }}>
          {t("audioMissing")}
        </p>
      ) : null}

      {!ctrl && audioSrc ? (
        <audio ref={audioRef} src={audioSrc} preload="none" data-testid={`${testIdPrefix}-audio-element`} />
      ) : null}
    </div>
  );
}

function ReelHub() {
  // Cream "tape teeth" fan that matches the printed cassette reel style.
  // Sized & positioned to overlay the painted reels — when the parent rotates,
  // it appears as if the reels themselves are spinning.
  return (
    <>
      {/* Outer dark base disc (covers painted reel underneath cleanly) */}
      <span
        aria-hidden
        className="absolute inset-0 block rounded-full"
        style={{
          background:
            "radial-gradient(circle at 38% 32%, #2A2521 0%, #15110F 60%, #0A0807 100%)",
          boxShadow:
            "inset 0 1px 2px rgba(255,255,255,0.06), inset 0 -2px 4px rgba(0,0,0,0.6), 0 0 0 1px rgba(0,0,0,0.4)",
        }}
      />
      {/* 8 cream "tape teeth" — matches a real cassette reel hub */}
      <span
        aria-hidden
        className="absolute inset-0 block rounded-full"
        style={{
          background:
            "conic-gradient(from 0deg, rgba(232,222,200,0.95) 0deg 14deg, transparent 14deg 45deg, rgba(232,222,200,0.95) 45deg 59deg, transparent 59deg 90deg, rgba(232,222,200,0.95) 90deg 104deg, transparent 104deg 135deg, rgba(232,222,200,0.95) 135deg 149deg, transparent 149deg 180deg, rgba(232,222,200,0.95) 180deg 194deg, transparent 194deg 225deg, rgba(232,222,200,0.95) 225deg 239deg, transparent 239deg 270deg, rgba(232,222,200,0.95) 270deg 284deg, transparent 284deg 315deg, rgba(232,222,200,0.95) 315deg 329deg, transparent 329deg 360deg)",
          mask: "radial-gradient(circle, transparent 28%, #000 32% 88%, transparent 94%)",
          WebkitMask:
            "radial-gradient(circle, transparent 28%, #000 32% 88%, transparent 94%)",
        }}
      />
      {/* Inner ring shading */}
      <span
        aria-hidden
        className="absolute inset-0 block rounded-full"
        style={{
          background:
            "radial-gradient(circle, transparent 30%, rgba(0,0,0,0.35) 36%, transparent 40%)",
        }}
      />
      {/* Dark hub w/ subtle highlight */}
      <span
        aria-hidden
        className="absolute left-1/2 top-1/2 block rounded-full"
        style={{
          width: "30%",
          height: "30%",
          transform: "translate(-50%, -50%)",
          background:
            "radial-gradient(circle at 38% 32%, #4A3F36 0%, #1F1B18 65%, #050403 100%)",
          boxShadow:
            "inset 0 1px 1px rgba(255,255,255,0.18), inset 0 -1px 1px rgba(0,0,0,0.8), 0 0 0 1px rgba(0,0,0,0.5)",
        }}
      />
    </>
  );
}



function StatusLed({ label, on, color }) {
  return (
    <div className="flex items-center gap-1.5">
      <span className="h-2.5 w-2.5 rounded-full transition-all duration-300" style={{ backgroundColor: on ? color : "rgba(255,255,255,0.12)", boxShadow: on ? `0 0 8px ${color}` : "none" }} />
      <span className="text-[9px] uppercase tracking-[0.25em]" style={{ color: "#9B8A75", fontWeight: 700 }}>{label}</span>
    </div>
  );
}

function MechBtn({ label, onClick, testId, ariaLabel, disabled, active, recColor, big, title }) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel}
      title={title}
      className={`relative flex items-center justify-center rounded-[4px] transition-all duration-100 focus:outline-none active:translate-y-[2px] ${big ? "h-12 text-[15px]" : "h-11 text-[12px]"}`}
      style={{
        background:
          active && big
            ? "linear-gradient(180deg, #5A4E40 0%, #2A2521 100%)"
            : "linear-gradient(180deg, #3A322C 0%, #1A1612 100%)",
        color: recColor ? "#FF4530" : active ? "#FFC85E" : "#D9C7A6",
        border: "1px solid #0A0807",
        boxShadow: "inset 0 1px 0 rgba(255,255,255,0.14), inset 0 -2px 0 rgba(0,0,0,0.55), 0 2px 4px rgba(0,0,0,0.5)",
        fontWeight: 700,
        letterSpacing: "0.5px",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.55 : 1,
      }}
    >
      {label}
    </button>
  );
}
